import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, FileText } from 'lucide-react';
import {
  MedicalRecordFormData,
  MedicalRecordUpdateRequest,
  MedicalRecord
} from '../../types/medicalRecord';
import { PetWithOwner } from '../../types/pet';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeaders } from '../../utils/authUtils';

interface MedicalRecordFormProps {
  mode: 'create' | 'edit';
  petId?: string;
  recordId?: string;
}

const MedicalRecordForm: React.FC<MedicalRecordFormProps> = ({ mode, petId: propPetId, recordId: propRecordId }) => {
  const navigate = useNavigate();
  const params = useParams<{ petId: string; recordId?: string }>();
  const petId = propPetId || params.petId;
  const recordId = propRecordId || params.recordId;
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copy_from_id');
  const { user } = useAuth();

  const [pet, setPet] = useState<PetWithOwner | null>(null);
  const [existingRecord, setExistingRecord] = useState<MedicalRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<Partial<MedicalRecordFormData>>({
    description: ''
  });

  useEffect(() => {
    if (petId) {
      fetchPet();
    }
    if (mode === 'edit' && recordId) {
      fetchExistingRecord(recordId);
    } else if (mode === 'create' && copyFromId) {
      fetchExistingRecord(copyFromId, true);
    } else {
      setLoading(false);
    }
  }, [petId, recordId, mode, copyFromId]);

  const fetchPet = async () => {
    if (!petId) return;

    try {
      const response = await fetch(API_ENDPOINTS.PETS.GET(petId), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pet information');
      }

      const petData = await response.json();
      setPet(petData);
    } catch (err) {
      console.error('Error fetching pet:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pet information');
    }
  };

  const fetchExistingRecord = async (id: string, isCopy: boolean = false) => {
    if (!id) return;

    try {
      const response = await fetch(API_ENDPOINTS.MEDICAL_RECORDS.GET(id), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch medical record');
      }

      const recordData = await response.json();
      setExistingRecord(recordData);

      // Populate form with existing description only
      setFormData({
        description: recordData.description || ''
      });
    } catch (err) {
      console.error('Error fetching medical record:', err);
      setError(err instanceof Error ? err.message : 'Failed to load medical record');
    } finally {
      setLoading(false);
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ description: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Only update the description field
      const updateData: Partial<MedicalRecordUpdateRequest> = {
        description: formData.description
      };

      const response = await fetch(API_ENDPOINTS.MEDICAL_RECORDS.UPDATE(recordId!), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update medical summary');
      }

      // Navigate back to pet detail page
      navigate(`/dashboard/pets/${petId}`);
    } catch (err) {
      console.error('Error updating medical summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to update medical summary');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = () => {
    return user?.role === 'ADMIN' ||
      user?.role === 'PRACTICE_ADMIN' ||
      user?.role === 'VET_STAFF';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  if (!canSubmit()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only veterinarians and administrators can manage medical records.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Edit Medical Summary
          </h1>
          {pet && (
            <p className="text-gray-600 mt-1">
              for {pet.name} ({pet.species})
            </p>
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white shadow-sm rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-blue-600" />
              Medical Summary
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Edit the AI-generated medical summary for this visit.
            </p>

            <textarea
              id="description"
              name="description"
              value={formData.description || ''}
              onChange={handleDescriptionChange}
              rows={12}
              placeholder="Medical summary..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 leading-relaxed"
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/pets/${petId}`)}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save Summary'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicalRecordForm;
